import { LOCATION_RECENCY_BANDS, LOCATION_RECENCY_EDGE_HOURS, type LocationRecencyBand } from '@pams/shared';
import { formatCount } from '@/lib/analyticsReport';
import type { Basemap } from './mapSpec';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL] EVERY WORD ON THE LOCATIONAL INTEL PAGE.
 *
 *  ⚠️ A COLOUR MEANS HOW LONG AGO THE PHONE LAST REPORTED, AND THE LEGEND SAYS EXACTLY THAT — never
 *  "on duty", never "active". The band is the SERVER's (`locationRecencyBandOf`, on the server's own
 *  receipt time), and these words are built from the same `LOCATION_RECENCY_EDGE_HOURS` it reads, so
 *  the words and the colour cannot disagree about where an edge is.
 *
 *  ⚠️ A DOT IS NOT WHERE THE OFFICER IS. It is where their phone last sent a location, which since
 *  [LOCATION-ANYTIME] includes an officer who opened the app while off duty — the grey dots, mostly.
 *  The note under the legend says so, because the map reads as "the force, now" otherwise.
 *
 *  ⚠️ NO WORD HERE NAMES AN OFFICER'S PHONE, AND THE CARD HAS NO ROW FOR ONE (the user's decision).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const { LAST_HOUR, LAST_SIX_HOURS } = LOCATION_RECENCY_EDGE_HOURS;

/* ⚠️ A `number` PARAMETER, not the constants directly: `LAST_HOUR` is the literal type `1`, so a ternary on
   it narrows its other branch to `never` — and that branch is the sentence for the day the edge moves. */
const withinLast = (hours: number): string => (hours === 1 ? 'within the last hour' : `within the last ${hours} hours`);
const between = (from: number, to: number): string => `between ${from} and ${to} hours ago`;
const moreThan = (hours: number): string => `more than ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

const listOf = (items: readonly string[]): string =>
  new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }).format(items);

const officers = (count: number): string => `${formatCount(count)} ${count === 1 ? 'officer' : 'officers'}`;

/** The clause after a count in the summary sentence: "4 within the last hour". */
const BAND_CLAUSE: Record<LocationRecencyBand, string> = {
  LAST_HOUR: withinLast(LAST_HOUR),
  LAST_SIX_HOURS: between(LAST_HOUR, LAST_SIX_HOURS),
  OLDER: moreThan(LAST_SIX_HOURS),
};

/** ⚠️ EXHAUSTIVE OVER THE BANDS, so a fourth band is a compile error here rather than a blank legend row. */
const BAND_WORDS: Record<LocationRecencyBand, string> = {
  LAST_HOUR: `Last reported ${BAND_CLAUSE.LAST_HOUR}`,
  LAST_SIX_HOURS: `Last reported ${BAND_CLAUSE.LAST_SIX_HOURS}`,
  OLDER: `Last reported ${BAND_CLAUSE.OLDER}`,
};

/** Said in the Map key and, on its own, over an empty map ([LOCATION-INTEL.1]). */
const NONE_REPORTED = 'No officer in your area has reported a location yet.';

export const LOCATION_INTEL_COPY = {
  title: 'Locational Intel',
  subtitle: 'Where the officers in your area last reported a location. Select a dot to see who it is.',
  updatedAt: (time: string): string => `Updated at ${time}.`,
  /** The header's one line of freshness, beside Refresh — the rest of the words live in the Map key. */
  updatedShort: (time: string): string => `Updated ${time}`,
  refreshFailed: 'The last refresh failed, so this is the previous read. Try Refresh again.',
  refreshFailedShort: 'Refresh failed',
  refresh: 'Refresh',

  /* [LOCATION-INTEL.1] The side panel became a dialog, so the map can take the whole area. */
  mapKey: 'Map key',
  mapKeyTitle: 'About this map',

  /* [LOCATION-INTEL.1] The toolbar on the map. */
  viewControlsLabel: 'Map view',
  centralKolkata: 'Central Kolkata',
  centralKolkataLabel: 'Centre the map on Central Kolkata',
  allOfficers: 'All officers',
  allOfficersLabel: 'Zoom to show every officer on the map',
  basemapLabel: 'Map style',
  /** ⚠️ EXHAUSTIVE OVER THE BASEMAPS, so a sixth style is a compile error here rather than a blank option. */
  basemap: {
    streets: 'Streets',
    satellite: 'Satellite',
    outdoors: 'Outdoors',
    light: 'Light',
    dark: 'Dark',
  } satisfies Record<Basemap, string>,

  band: BAND_WORDS,
  legendTitle: 'What a colour means',
  legendCluster: 'A numbered circle is several officers close together. Select it, or zoom in, to separate them.',
  /*
   * ⚠️ The note the brief asks for: a dot is a phone's last report, and grey is usually somebody off duty.
   * [LOCATION-INTEL.2] In the brief's own words (§4d). It said "an officer who opened Duty Mitra while off duty", and
   * in the brief's terms that draws no dot at all: Duty Mitra sends only while a duty is in progress — it is the
   * mobile app that sends at any open. A grey dot is an old report, whichever app sent it.
   */
  legendNote:
    'A dot is where the officer’s phone last reported its location, not where the officer is now. A grey dot is usually a Duty Mitra user who is not on duty now.',

  noneReported: NONE_REPORTED,
  summary: (total: number, counts: Record<LocationRecencyBand, number>): string =>
    total === 0
      ? NONE_REPORTED
      : `${officers(total)} in your area ${total === 1 ? 'has' : 'have'} reported a location: ${listOf(
          LOCATION_RECENCY_BANDS.map((band) => `${formatCount(counts[band])} ${BAND_CLAUSE[band]}`),
        )}.`,
  /** Past the page bound: the map says how many dots it drew, never fewer in silence. */
  truncated: (shown: number, total: number): string =>
    `The map shows ${officers(shown)} of ${formatCount(total)}. The rest could not be drawn on one page.`,
  /** [LOCATION-INTEL.1] Short of the count with no bound reached: the list moved during the read. */
  shortRead: (shown: number, total: number): string =>
    `The map shows ${officers(shown)} of ${formatCount(total)}: the list changed while it was read. Refresh to draw everyone.`,

  loading: 'Loading the map',
  loadingPoints: 'Loading officer locations',
  errorTitle: 'The map could not load',
  errorDescription: 'Something went wrong while reading the officers’ locations. Try again in a moment.',
  retry: 'Try again',

  notConfiguredTitle: 'The map is not configured',
  notConfiguredDescription:
    'This deployment has no Mapbox token, so no map can be drawn. Ask your administrator to set one.',

  mapFailedTitle: 'The map could not be drawn',
  mapFailedWebGl: 'This browser could not start the map. It needs WebGL, which may be switched off.',
  mapFailedToken: 'Mapbox refused the map’s token. Ask your administrator to check it.',
  /* [LOCATION-INTEL.1] Not the officers' locations: the map program itself did not arrive. */
  mapFailedLoad: 'The map could not be downloaded. Check the connection, then reload the page.',
  /* [LOCATION-INTEL.1] The basemap's style request failed for any reason but a refused token. */
  mapFailedStyle:
    'The map style could not be loaded from Mapbox. Check that this device can reach api.mapbox.com, then try again or choose another map style.',
  mapLabel: 'Map of where officers last reported a location',

  card: {
    loading: 'Loading the officer’s details',
    rank: 'Rank',
    unit: 'Unit',
    division: 'Division',
    lastReported: 'Last reported',
    lastReportedValue: (when: string, band: LocationRecencyBand): string => `${when} (${BAND_CLAUSE[band]})`,
    none: '—',
    dutyHeading: 'On duty now',
    when: 'When',
    dutyPlace: 'Duty place',
    dutyPlaces: 'Duty places',
    reportingPlace: 'Reporting place',
    placesWithheld: 'The places are not shown: this duty is on an order outside your area.',
    /* A 404 on a click — the dot's officer left the map between the map's read and this one. */
    goneTitle: 'This officer is no longer on the map',
    goneDescription:
      'Their location may have aged out, or they may have moved out of your area. Refresh the map to see it as it is now.',
    errorTitle: 'The officer’s details could not load',
    retry: 'Try again',
  },
} as const;
